import React from 'react';
import GooglePlacesAddressInput from 'components/GooglePlacesAddressInput';
import closeIcon from 'assets/icons/closeIcon.svg';
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';
import { scaleMotionProps2 } from 'constants/animationProps';
import fundmoreai from 'assets/fundmoreai.png';
import { useTranslation } from 'react-i18next';
import styles from '../../../styles/features/onboarding-layout.module.css';
/**
 * Renders the Page2 component.
 * 
 * @param {Object} props - The component props.
 * @param {string} props.user - The user name.
 * @param {function} props.setAddress - The function to set the address.
 * @param {string} props.address - The current address.
 * @param {function} props.setPage - The function to set the current page.
 * @param {boolean} props.isLoading - Indicates if the page is loading.
 * @param {function} props.setDirection - The function to set the direction.
 * @param {boolean} props.direction - The current direction.
 * @returns {JSX.Element} The rendered Page2 component.
 */
const Page2 = ({
  user,
  setAddress,
  address,
  setPage,
  isLoading,
  setDirection,
  direction
}) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const handleAddressChange = (value) => {
    setAddress(value);
  };

  return (
    <div className={styles.contentWrapper}>
      <AnimatePresence mode="wait">
        <motion.div
          {...scaleMotionProps2}
          className={`${styles.formContainer} ${styles.fadeInContent}`}
        >
          <div className={styles.logoContainer}>
            <img src={fundmoreai} alt="Fundmore" className={styles.logoIcon} />
            <div className={styles.logoText}>
              <span className={styles.logoTextLight}>fund</span>
              <span className={styles.logoTextBold}>more</span>
              <span className={styles.logoTextGreen}>.ai</span>
            </div>
          </div>
          <span className={styles.srOnly}>
            {t('onboarding:greeting', { name: user })}
          </span>

          <div className={styles.progressDotsContainer}>
            <div className={`${styles.progressDot} ${styles.progressDotInactive}`} />
            <div className={`${styles.progressDot} ${styles.progressDotActive}`} />
          </div>

          <h2 className={styles.heading}>
            {t('onboarding:locationQuestion')}
          </h2>
          <p className={styles.subheading}>
            {t('onboarding:locationSubtitle')}
          </p>

          <div className={styles.optionsContainer}>
            <div className={styles.inputWrapper}>
              <GooglePlacesAddressInput
                value={address}
                placeholder={t('onboarding:locationPlaceholder')}
                onChange={handleAddressChange}
                onAddressSelected={(parsedAddress) => {
                  if (!parsedAddress) {
                    return;
                  }
                  const resolvedAddress =
                    parsedAddress.formattedAddress ||
                    parsedAddress.street ||
                    address;
                  if (resolvedAddress) {
                    setAddress(resolvedAddress);
                  }
                }}
              />
              {address && (
                <img
                  src={closeIcon}
                  alt=""
                  className={styles.clearIcon}
                  onClick={() => setAddress('')}
                />
              )}
            </div>
          </div>

          <div className={`${styles.buttonContainer} ${styles.buttonGroup}`}>
            <button
              type="button"
              onClick={() => {
                setPage(1);
                setDirection(false);
              }}
              className={`${styles.button} ${styles.buttonSecondary} ${styles.buttonFlexOne}`}
            >
              {t('common:buttons.back')}
            </button>
            <button
              type="button"
              disabled={address === ''}
              onClick={() => {
                setPage(3);
                setDirection(true);
              }}
              className={`${styles.button} ${styles.buttonPrimary} ${styles.buttonFlexTwo}`}
            >
              {isLoading && direction
                ? t('common:buttons.pleaseWait')
                : t('common:buttons.continue')}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Page2;
